import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Typography, Tooltip, Space } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;

/**
 * AnnotatedDocument component for displaying text with highlighted annotations
 * 
 * Supports TWO highlighting modes:
 * 1. Entity-based (word matching): Uses data.entities with word arrays
 * 2. Position-based (char positions): Uses data.annotations with char_start/char_end
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing:
 *   - text: The full document text
 *   - title: Optional document title
 *   - entities: Object mapping entity names to {type, words[]} (word-based highlighting)
 *   - annotations: Array of {char_start, char_end, label, type, color} (position-based highlighting)
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const AnnotatedDocument = ({
  data,
  title = "Document",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // State to hold processed text with annotations
  const [processedContent, setProcessedContent] = useState(null);
  
  // State for entity type colors (word-based mode)
  const [typeColors, setTypeColors] = useState({});
  
  // State to track unique entity types
  const [entityTypes, setEntityTypes] = useState([]);
  
  // Ref for scrolling to highlighted sections
  const contentRef = useRef(null);
  const highlightRefs = useRef({});
  
  // State for tracking which annotation to scroll to
  const [scrollToAnnotation, setScrollToAnnotation] = useState(null);

  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  
  // Component data sync from backend
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      const refreshComponent = () => {
        console.log(`Refreshing AnnotatedDocument: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      const unregister = registerComponent(refreshComponent);
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Define a set of distinctive colors for entity types
  const colorPalette = [
    '#FFEB3B', // Yellow (primary evidence color)
    '#66FF00', // Green
    '#FF9800', // Orange
    '#2196F3', // Blue
    '#F44336', // Red
    '#8E44AD', // Purple
    '#795548', // Brown
    '#009688', // Teal
    '#FF5722', // Deep Orange
    '#607D8B', // Blue Grey
    '#E91E63', // Pink
    '#3F51B5', // Indigo
    '#CDDC39', // Lime
    '#FFC107', // Amber
    '#00BCD4', // Cyan
    '#9C27B0'  // Deep Purple
  ];

  // Scroll to first annotation when annotations change
  useEffect(() => {
    if (data?.annotations && data.annotations.length > 0 && scrollToAnnotation === null) {
      // Set to scroll to first annotation
      setScrollToAnnotation(0);
    }
  }, [data?.annotations]);

  // Perform scroll when scrollToAnnotation is set
  useEffect(() => {
    if (scrollToAnnotation !== null && highlightRefs.current[scrollToAnnotation]) {
      setTimeout(() => {
        highlightRefs.current[scrollToAnnotation]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [scrollToAnnotation, processedContent]);

  // Extract entity types and assign colors (for word-based mode)
  useEffect(() => {
    if (!data || !data.entities) return;
    
    const types = new Set();
    Object.values(data.entities).forEach(entityInfo => {
      if (entityInfo.type) {
        types.add(entityInfo.type);
      }
    });
    
    const uniqueTypes = Array.from(types);
    setEntityTypes(uniqueTypes);
    
    const colors = {};
    uniqueTypes.forEach((type, index) => {
      colors[type] = colorPalette[index % colorPalette.length];
    });
    
    setTypeColors(colors);
  }, [data]);

  // Process the text and annotations
  useEffect(() => {
    if (!data || !data.text) {
      setProcessedContent(null);
      return;
    }
    
    // Debug logging for annotation flow
    console.log('AnnotatedDocument: Processing data update', {
      hasText: !!data.text,
      textType: typeof data.text,
      textLength: typeof data.text === 'string' ? data.text.length : 'N/A',
      hasAnnotations: !!(data.annotations && data.annotations.length > 0),
      annotationCount: data.annotations?.length || 0,
      annotations: data.annotations || [],
      hasEntities: !!(data.entities && Object.keys(data.entities).length > 0),
      updateCounter
    });
    
    // Debug: Log the type of data.text to help diagnose issues
    if (typeof data.text !== 'string') {
      console.log('AnnotatedDocument: data.text is not a string:', typeof data.text, data.text);
    }
    
    // Determine which mode to use
    const hasPositionAnnotations = data.annotations && Array.isArray(data.annotations) && data.annotations.length > 0;
    const hasEntityAnnotations = data.entities && Object.keys(data.entities).length > 0;
    
    console.log('AnnotatedDocument: Mode selection', {
      hasPositionAnnotations,
      hasEntityAnnotations,
      willUsePositionBased: hasPositionAnnotations,
      willUseWordBased: !hasPositionAnnotations && hasEntityAnnotations && Object.keys(typeColors).length > 0,
      willUsePlainText: !hasPositionAnnotations && (!hasEntityAnnotations || Object.keys(typeColors).length === 0)
    });
    
    if (hasPositionAnnotations) {
      // Position-based highlighting (char_start/char_end)
      console.log('AnnotatedDocument: Using position-based annotations', data.annotations);
      const content = processPositionBasedAnnotations(data.text, data.annotations);
      setProcessedContent(content);
    } else if (hasEntityAnnotations && Object.keys(typeColors).length > 0) {
      // Word-based highlighting (entities with words arrays)
      const content = processWordBasedAnnotations(data.text, data.entities);
      setProcessedContent(content);
    } else {
      // No annotations - just display plain text with section parsing
      const content = processPlainText(data.text);
      setProcessedContent(content);
    }
  }, [data, typeColors, updateCounter]);

  /**
   * Process text with position-based annotations (char_start/char_end)
   * This is used for evidence highlighting when clicking on codes
   * MAINTAINS section formatting while applying highlights
   */
  const processPositionBasedAnnotations = (text, annotations) => {
    if (!text || !annotations || annotations.length === 0) {
      return processPlainText(text);
    }

    // Ensure text is a string
    let textString = text;
    if (typeof text === 'object') {
      textString = text.full_text || text.text || text.content || '';
    }
    
    if (typeof textString !== 'string') {
      console.warn('AnnotatedDocument: text is not a string in processPositionBasedAnnotations');
      return processPlainText(text);
    }

    // Sort annotations by char_start to process in order
    const sortedAnnotations = [...annotations].sort((a, b) => a.char_start - b.char_start);
    
    // Merge overlapping annotations
    const mergedAnnotations = mergeOverlappingAnnotations(sortedAnnotations);
    
    // Parse sections by finding headers
    const headerPattern = /^([A-Z][A-Z\s]*[A-Z]):?\s*/gm;
    const headers = [];
    let match;
    
    while ((match = headerPattern.exec(textString)) !== null) {
      headers.push({
        title: match[1].trim(),
        start: match.index,
        headerEnd: match.index + match[0].length
      });
    }
    
    // If no sections found, render as single block with highlights
    if (headers.length === 0) {
      return (
        <div ref={contentRef}>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
            {renderTextWithHighlights(textString, mergedAnnotations, 0)}
          </Paragraph>
        </div>
      );
    }
    
    // Build sections with their content ranges
    const sections = headers.map((header, index) => {
      const contentStart = header.headerEnd;
      const contentEnd = index < headers.length - 1 
        ? headers[index + 1].start 
        : textString.length;
      
      const rawContent = textString.substring(contentStart, contentEnd);
      // Calculate leading whitespace offset for position adjustment
      const leadingWhitespace = rawContent.length - rawContent.trimStart().length;
      
      return {
        title: header.title,
        contentStart,
        contentEnd,
        leadingWhitespace,
        content: rawContent.trim()
      };
    });
    
    // Render sections with highlights
    return (
      <div ref={contentRef}>
        {sections.map((section, sectionIndex) => {
          // Find annotations that fall within this section's content
          const sectionAnnotations = mergedAnnotations.filter(ann => 
            ann.char_start >= section.contentStart && ann.char_start < section.contentEnd
          ).map(ann => ({
            ...ann,
            // Adjust positions to be relative to section content start, accounting for trimmed whitespace
            char_start: ann.char_start - section.contentStart - section.leadingWhitespace,
            char_end: Math.min(ann.char_end, section.contentEnd) - section.contentStart - section.leadingWhitespace
          })).filter(ann => ann.char_start >= 0 && ann.char_end > ann.char_start); // Filter out invalid positions
          
          return (
            <div key={`section-${sectionIndex}`} style={{ marginBottom: '20px' }}>
              <Title level={5} style={{ marginBottom: '8px' }}>
                {section.title}:
              </Title>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                {renderTextWithHighlights(section.content, sectionAnnotations, sectionIndex * 1000)}
              </Paragraph>
            </div>
          );
        })}
      </div>
    );
  };
  
  /**
   * Helper function to render text with highlight spans
   * Used by processPositionBasedAnnotations to apply highlights within sections
   */
  const renderTextWithHighlights = (text, annotations, keyOffset = 0) => {
    if (!annotations || annotations.length === 0) {
      return text;
    }
    
    const chunks = [];
    let lastEnd = 0;
    
    annotations.forEach((ann, index) => {
      // Ensure we don't go out of bounds
      const start = Math.max(0, Math.min(ann.char_start, text.length));
      const end = Math.max(0, Math.min(ann.char_end, text.length));
      
      if (start >= end) return; // Skip invalid annotations
      
      // Add text before this annotation
      if (start > lastEnd) {
        const beforeText = text.substring(lastEnd, start);
        chunks.push(
          <span key={`text-${keyOffset}-${lastEnd}`}>{beforeText}</span>
        );
      }
      
      // Add the highlighted annotation
      const highlightedText = text.substring(start, end);
      const backgroundColor = ann.color || '#FFEB3B';
      const textColor = isLightColor(backgroundColor) ? 'black' : 'white';
      
      const tooltipContent = (
        <div>
          {ann.label && <div><b>Evidence:</b> {ann.label}</div>}
          {ann.type && <div><b>Type:</b> {ann.type}</div>}
          {ann.section && <div><b>Section:</b> {ann.section}</div>}
          {ann.code && <div><b>Code:</b> {ann.code}</div>}
        </div>
      );
      
      chunks.push(
        <Tooltip title={tooltipContent} key={`highlight-${keyOffset}-${start}`}>
          <span
            ref={el => highlightRefs.current[keyOffset + index] = el}
            style={{
              backgroundColor,
              color: textColor,
              padding: '2px 4px',
              borderRadius: '3px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
          >
            {highlightedText}
          </span>
        </Tooltip>
      );
      
      lastEnd = end;
    });
    
    // Add any remaining text after the last annotation
    if (lastEnd < text.length) {
      chunks.push(
        <span key={`text-${keyOffset}-${lastEnd}`}>{text.substring(lastEnd)}</span>
      );
    }
    
    return chunks.length > 0 ? chunks : text;
  };

  /**
   * Merge overlapping annotations to prevent rendering issues
   */
  const mergeOverlappingAnnotations = (annotations) => {
    if (annotations.length === 0) return [];
    
    const merged = [{ ...annotations[0] }];
    
    for (let i = 1; i < annotations.length; i++) {
      const current = annotations[i];
      const last = merged[merged.length - 1];
      
      // Check if current overlaps with last
      if (current.char_start <= last.char_end) {
        // Merge by extending the end
        last.char_end = Math.max(last.char_end, current.char_end);
        // Combine labels if different
        if (current.label && current.label !== last.label) {
          last.label = last.label ? `${last.label}, ${current.label}` : current.label;
        }
      } else {
        merged.push({ ...current });
      }
    }
    
    return merged;
  };

  /**
   * Process text with word-based annotations (entity matching)
   * This is the original highlighting mode
   */
  const processWordBasedAnnotations = (text, entities) => {
    // Ensure text is a string
    let textString = text;
    if (typeof text === 'object') {
      textString = text.full_text || text.text || text.content || '';
    }
    
    if (typeof textString !== 'string') {
      console.warn('AnnotatedDocument: text is not a string in processWordBasedAnnotations');
      return null;
    }
    
    // Parse sections by finding headers and extracting content between them
    const headerPattern = /^([A-Z][A-Z\s]*[A-Z]):?\s*/gm;
    const headers = [];
    let match;
    
    while ((match = headerPattern.exec(textString)) !== null) {
      headers.push({
        title: match[1].trim(),
        start: match.index,
        headerEnd: match.index + match[0].length
      });
    }
    
    const sections = [];
    
    if (headers.length === 0) {
      sections.push({
        title: 'Document',
        content: highlightEntitiesInText(textString, entities)
      });
    } else {
      headers.forEach((header, index) => {
        const contentStart = header.headerEnd;
        const contentEnd = index < headers.length - 1 
          ? headers[index + 1].start 
          : textString.length;
        const sectionContent = textString.substring(contentStart, contentEnd).trim();
        
        sections.push({
          title: header.title,
          content: highlightEntitiesInText(sectionContent, entities)
        });
      });
    }
    
    return (
      <div ref={contentRef}>
        {sections.map((section, index) => (
          <div key={`section-${index}`} style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ marginBottom: '8px' }}>
              {section.title}:
            </Title>
            <Paragraph>
              {section.content}
            </Paragraph>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Highlight entities in text using word matching
   */
  const highlightEntitiesInText = (text, entities) => {
    if (!text || !entities) return text;
    
    // Ensure text is a string
    if (typeof text !== 'string') {
      console.warn('AnnotatedDocument: text is not a string in highlightEntitiesInText');
      return String(text);
    }
    
    const termsToHighlight = [];
    
    Object.entries(entities).forEach(([entityName, entityInfo]) => {
      const type = entityInfo.type;
      const words = entityInfo.words || [];
      
      words.forEach(word => {
        if (!word || word.trim() === '') return;
        
        termsToHighlight.push({
          term: word,
          entityName,
          type,
          length: word.length
        });
      });
    });
    
    termsToHighlight.sort((a, b) => b.length - a.length);
    
    const chunks = [];
    let remainingText = text;
    let chunkIndex = 0;
    
    while (remainingText.length > 0) {
      let foundMatch = false;
      
      for (const { term, entityName, type } of termsToHighlight) {
        const regex = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i');
        const match = remainingText.match(regex);
        
        if (match) {
          const index = match.index;
          const matchedText = match[0];
          
          if (index > 0) {
            chunks.push({
              type: 'text',
              content: remainingText.substring(0, index),
              key: `text-${chunkIndex++}`
            });
          }
          
          chunks.push({
            type: 'highlight',
            content: matchedText,
            entityName,
            entityType: type,
            color: typeColors[type],
            key: `highlight-${chunkIndex++}`
          });
          
          remainingText = remainingText.substring(index + matchedText.length);
          foundMatch = true;
          break;
        }
      }
      
      if (!foundMatch) {
        chunks.push({
          type: 'text',
          content: remainingText,
          key: `text-${chunkIndex++}`
        });
        remainingText = '';
      }
    }
    
    return chunks.map((chunk) => {
      if (chunk.type === 'text') {
        return <span key={chunk.key}>{chunk.content}</span>;
      } else {
        const color = chunk.color || '#9E9E9E';
        const backgroundColor = color;
        const textColor = isLightColor(color) ? 'black' : 'white';
        
        const tooltipContent = (
          <div>
            <div><b>Entity:</b> {chunk.entityName}</div>
            <div><b>Type:</b> {chunk.entityType}</div>
          </div>
        );
        
        return (
          <Tooltip title={tooltipContent} key={chunk.key}>
            <span
              style={{
                backgroundColor,
                color: textColor,
                padding: '1px 2px',
                borderRadius: '2px',
                cursor: 'pointer'
              }}
            >
              {chunk.content}
            </span>
          </Tooltip>
        );
      }
    });
  };

  /**
   * Process plain text without annotations - just parse sections
   */
  const processPlainText = (text) => {
    // Ensure text is a valid string
    if (!text) return null;
    
    // Handle case where text might be an object (e.g., from trace file)
    let textString = text;
    if (typeof text === 'object') {
      // Try to extract text from common object structures
      textString = text.full_text || text.text || text.content || JSON.stringify(text);
    }
    
    if (typeof textString !== 'string') {
      console.warn('AnnotatedDocument: text is not a string:', typeof textString, textString);
      return null;
    }
    
    // Parse sections by finding headers and extracting content between them
    // Header pattern: Line starting with 2+ uppercase letters followed by colon
    const headerPattern = /^([A-Z][A-Z\s]*[A-Z]):?\s*/gm;
    const headers = [];
    let match;
    
    while ((match = headerPattern.exec(textString)) !== null) {
      headers.push({
        title: match[1].trim(),
        start: match.index,
        headerEnd: match.index + match[0].length
      });
    }
    
    if (headers.length === 0) {
      // No sections found, display as plain text
      return (
        <div ref={contentRef}>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{textString}</Paragraph>
        </div>
      );
    }
    
    // Extract content for each section
    const sections = headers.map((header, index) => {
      const contentStart = header.headerEnd;
      const contentEnd = index < headers.length - 1 
        ? headers[index + 1].start 
        : textString.length;
      const content = textString.substring(contentStart, contentEnd).trim();
      
      return {
        title: header.title,
        content: content
      };
    });
    
    return (
      <div ref={contentRef}>
        {sections.map((section, index) => (
          <div key={`section-${index}`} style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ marginBottom: '8px' }}>
              {section.title}:
            </Title>
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
              {section.content}
            </Paragraph>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to escape regex special characters
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };
  
  // Helper function to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color) return true;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return brightness > 0.5;
  };
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;

  // Build legend for annotations
  const renderLegend = () => {
    // For position-based annotations
    if (data?.annotations && data.annotations.length > 0) {
      const uniqueTypes = [...new Set(data.annotations.map(a => a.type).filter(Boolean))];
      const uniqueColors = [...new Set(data.annotations.map(a => a.color).filter(Boolean))];
      
      if (uniqueTypes.length === 0 && uniqueColors.length <= 1) {
        // Simple legend for single-color evidence
        return (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: '#f5f5f5',
            borderRadius: '4px'
          }}>
            <Text strong>Legend:</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <div 
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: uniqueColors[0] || '#FFEB3B',
                  borderRadius: '3px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
              />
              <Text>Evidence Source ({data.annotations.length} highlight{data.annotations.length !== 1 ? 's' : ''})</Text>
            </div>
          </div>
        );
      }
      
      // Multi-type legend
      return (
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}>
          <Text strong>Evidence Types:</Text>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '8px'
          }}>
            {uniqueTypes.map((type, index) => {
              const ann = data.annotations.find(a => a.type === type);
              const color = ann?.color || colorPalette[index % colorPalette.length];
              return (
                <div 
                  key={`legend-${type}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <div 
                    style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: color,
                      borderRadius: '2px'
                    }}
                  />
                  <Text>{type}</Text>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    // For word-based annotations
    if (entityTypes.length > 0) {
      return (
        <div style={{ 
          marginTop: '24px', 
          padding: '12px', 
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}>
          <Text strong>Entity Types:</Text>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '8px'
          }}>
            {entityTypes.map(type => (
              <div 
                key={`legend-${type}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <div 
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: typeColors[type] || '#9E9E9E',
                    borderRadius: '2px'
                  }}
                />
                <Text>{type}</Text>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {title}
                {data?.annotations && data.annotations.length > 0 && (
                  <span style={{ 
                    marginLeft: '12px', 
                    fontSize: '14px', 
                    color: '#666',
                    fontWeight: 'normal'
                  }}>
                    ({data.annotations.length} evidence highlight{data.annotations.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
            ),
            children: (
              <div>
                {/* Display document title if available */}
                {data && data.title && (
                  <Title level={4} style={{ marginBottom: '24px' }}>
                    {data.title}
                  </Title>
                )}
                
                {/* Display processed content */}
                <div style={{ 
                  maxHeight: 'calc(100vh - 400px)', 
                  overflow: 'auto',
                  padding: '8px',
                  backgroundColor: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8'
                }}>
                  {processedContent || (
                    <Text type="secondary">No content to display</Text>
                  )}
                </div>
                
                {/* Display legend */}
                {renderLegend()}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button 
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                      style={{
                        padding: '4px 12px',
                        cursor: currentVersion <= Math.min(...versionInfo.available_versions) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <LeftOutlined /> Previous
                    </button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <button 
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                      style={{
                        padding: '4px 12px',
                        cursor: currentVersion >= Math.max(...versionInfo.available_versions) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next <RightOutlined />
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default AnnotatedDocument;